import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import FilterBar from "@/pages/category/components/FilterBar";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { genreOptions, getByType, regionOptions, yearOptions, type VideoType } from "@/mocks/videos";

interface VideoBrowserProps {
  type: VideoType;
  variant?: "category" | "list";
}

const PER_PAGE = 6;
const SORT_OPTIONS = [
  { key: "latest", label: "最新更新" },
  { key: "popularity", label: "最多热度" },
  { key: "rating", label: "评分最高" },
];

const parseList = (raw: string | null): string[] => (raw ? raw.split(",").filter(Boolean) : []);

export default function VideoBrowser({ type, variant = "category" }: VideoBrowserProps) {
  const [params, setParams] = useSearchParams();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const selected: Record<string, string[]> = {
    regions: parseList(params.get("region")),
    years: parseList(params.get("year")),
    genres: parseList(params.get("genre")),
    tags: parseList(params.get("tag")),
  };
  const sort = params.get("sort") || "latest";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const typeVideos = getByType(type);
  const allTags = useMemo(() => Array.from(new Set(typeVideos.flatMap((v) => v.tags))), [typeVideos]);

  const groups = [
    { key: "regions" as const, label: "地区", options: regionOptions.filter((r) => r !== "全部") },
    { key: "years" as const, label: "年份", options: yearOptions.filter((y) => y !== "全部") },
    { key: "genres" as const, label: "题材", options: genreOptions[type].filter((g) => g !== "全部") },
    ...(variant === "list" ? [{ key: "tags" as const, label: "标签", options: allTags }] : []),
  ];

  const filtered = useMemo(() => {
    let list = typeVideos.filter((v) => {
      if (selected.regions.length && !selected.regions.includes(v.region)) return false;
      if (selected.years.length && !selected.years.includes(String(v.year))) return false;
      if (selected.genres.length && !v.genres.some((g) => selected.genres.includes(g))) return false;
      if (selected.tags.length && !v.tags.some((t) => selected.tags.includes(t))) return false;
      return true;
    });
    if (sort === "popularity") list = [...list].sort((a, b) => b.popularity - a.popularity);
    else if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else list = [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return list;
  }, [typeVideos, selected.regions, selected.years, selected.genres, selected.tags, sort]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const activeCount = selected.regions.length + selected.years.length + selected.genres.length + selected.tags.length;

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: false });
  };

  const toggle = (key: string, value: string) => {
    const current = selected[key] || [];
    const nextVals = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    updateParam(key, nextVals.length ? nextVals.join(",") : null);
  };

  const reset = () => {
    const next = new URLSearchParams();
    if (sort !== "latest") next.set("sort", sort);
    setParams(next, { replace: false });
  };

  const visible = isDesktop ? filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE) : filtered.slice(0, page * PER_PAGE);

  return (
    <div className="w-full px-4 md:px-8 py-8">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <p className="text-sm text-foreground-600">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部内容
        </p>
        <div className="flex items-center gap-1 bg-background-100 rounded-md p-1">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => updateParam("sort", opt.key === "latest" ? null : opt.key)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === opt.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <FilterBar groups={groups} selected={selected} onToggle={toggle} onReset={reset} activeCount={activeCount} />

      {filtered.length === 0 ? (
        <EmptyState
          title="没有找到符合条件的内容"
          description="当前筛选条件下暂无匹配内容，试试调整或重置筛选条件，或浏览其他分类。"
          actionLabel="重置筛选"
          onAction={reset}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 mt-6">
            {visible.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          <div className="hidden md:block">
            <Pagination current={page} total={totalPages} onChange={(p) => updateParam("page", String(p))} />
          </div>

          {!isDesktop && page < totalPages && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => updateParam("page", String(page + 1))}
                className="px-8 py-3 rounded-md border border-background-200 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                加载更多
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}